import { GraphQLClient, ClientError } from 'graphql-request';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { RequestOptions, Variables } from 'graphql-request';
import * as Sentry from '@sentry/react';
import { keycloak, updateTokenOrTimeout } from './keycloak';
import { areExpectedGqlErrors } from './gql_expected_errors';
import { graphqlErrorReport } from './sentry_scrub';

// Gateway has CORS configured for the dev origin — call it directly in all environments.
const rawEndpoint = (import.meta.env.VITE_GRAPHQL_URL ?? 'http://localhost:4000/graphql').trim();

// A schemeless value (e.g. a Vercel env var accidentally set to "api.entite.io" instead of
// "https://api.entite.io/graphql") does NOT fail fast: fetch() silently treats it as a path
// relative to the CURRENT page, hitting this app's own origin (Vercel's SPA fallback returns
// 200/HTML) instead of the API. graphql-request then fails to parse that as JSON and rejects
// with a plain (non-ClientError) Error — which responseMiddleware's `instanceof ClientError`
// branch below did not log at all. The result: every GraphQL query in the app fails with zero
// console output and no entry that looks like "the API call" in the Network tab — this exact
// misconfiguration cost hours to diagnose live in prod. Fail loud instead.
if (!/^https?:\/\//.test(rawEndpoint)) {
  const msg = `[graphql_client] VITE_GRAPHQL_URL is not an absolute URL (missing scheme): "${rawEndpoint}"`;
  console.error(msg);
  Sentry.captureException(new Error(msg));
}
const endpoint = rawEndpoint;

/**
 * Extract the operation name from a GraphQL query string or TypedDocumentNode.
 */
function extractOperationName(query: unknown): string | undefined {
  // String query
  if (typeof query === 'string') {
    const match = query.match(/(?:query|mutation|subscription)\s+(\w+)/);
    return match?.[1];
  }
  // TypedDocumentNode
  const doc = query as { definitions?: Array<{ name?: { value?: string } }> };
  return doc?.definitions?.[0]?.name?.value;
}

export const graphqlClient = new GraphQLClient(endpoint, {
  // The gateway resolves the active-context session per request (keyed by the KC
  // sid in the bearer token); send credentials so any session cookie rides along
  // too (T17 handover #5).
  credentials: 'include',
  requestMiddleware: async (request) => {
    // EW-FIX — updateToken() rejecting used to be swallowed silently: the .catch()
    // called keycloak.login() (a full-page redirect) with zero logging, so any
    // request that hit this path vanished from the Network tab entirely — the
    // symptom looked like "the app never even tries to call the API" rather than
    // "token refresh failed". Log loudly FIRST (console + Sentry, so this is
    // diagnosable from a user's browser without reproducing it live), THEN redirect.
    //
    // EW-FIX-95 — `updateTokenOrTimeout`, not the bare `keycloak.updateToken()`: a
    // HANG (never resolves, never rejects — see its docblock in keycloak.ts) used
    // to reach none of the logging below and never call fetch() either, so a
    // polling query (e.g. the entity-import wizard) would stall with zero trace.
    try {
      await updateTokenOrTimeout(30);
    } catch (err) {
      console.error('[Keycloak] updateToken(30) failed before a GraphQL request:', err);
      Sentry.captureException(err, {
        tags: { context: 'keycloak_update_token', graphql_url: request.url },
      });
      try {
        await keycloak.login();
      } catch (loginErr) {
        // login() itself failing is the actually-silent failure mode this
        // guards: it does not navigate, so without this the request would hang
        // or throw with no indication a redirect was ever attempted.
        console.error('[Keycloak] login() redirect itself failed:', loginErr);
        Sentry.captureException(loginErr, { tags: { context: 'keycloak_login_redirect' } });
      }
      // login() navigates away on success; if we are still here (it failed, or
      // the navigation has not applied yet), do not let the request proceed
      // with a stale/absent token — fail it explicitly instead of a silent hang.
      throw err;
    }

    // EW-FIX-45 follow-up — updateToken() was NOT the failure (confirmed live: no
    // '[Keycloak] updateToken' log appears, yet the fetch() below still never fires
    // — zero rows in the Network tab beyond the unrelated Sentry/token calls). That
    // means something in THIS block throws synchronously and is swallowed: an async
    // requestMiddleware that throws before returning rejects graphql-request's
    // `.request()` promise directly, with no responseMiddleware invocation (that
    // only runs for a completed request/response cycle) and, until now, no logging
    // at all here. Wrapped so a genuine bug here is finally visible instead of a
    // silent zero-network failure indistinguishable from "never tried".
    try {
      // Append operation name as query param for network tab visibility
      let url = request.url;
      const operationName = extractOperationName(
        request.body ? JSON.parse(request.body as string)?.query : undefined,
      );
      if (operationName && url) {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}op=${operationName}`;
      }

      return {
        ...request,
        url,
        headers: {
          ...request.headers,
          'content-type': 'application/json',
          'apollo-require-preflight': 'true',
          authorization: `Bearer ${keycloak.token}`,
        },
      };
    } catch (err) {
      console.error('[graphql_client] requestMiddleware threw before fetch() ran:', err);
      Sentry.captureException(err, {
        tags: { context: 'graphql_request_middleware', graphql_url: request.url },
      });
      throw err;
    }
  },

  responseMiddleware: (response) => {
    // graphql-request passes errors as response with errors property
    if (response instanceof Error) {
      const clientError = response instanceof ClientError ? response : null;
      const errors = clientError?.response?.errors;
      const operationName = clientError?.request?.query
        ? extractOperationName(clientError.request.query)
        : 'unknown';

      if (errors?.length) {
        // ENT-445 F-001: an error that is the operation's expected domain state
        // (e.g. the not-found family on EntityPolicyAssignment for a fresh
        // entity with no compliance profile) is an empty state the calling hook
        // maps to null — not a failure. Skip the console noise + Sentry for
        // those; any other (or mixed) error response still logs loudly.
        if (areExpectedGqlErrors(operationName, errors)) return;

        // Structured console log for debugging
        console.error(
          `[GraphQL Error] ${operationName}:`,
          errors.map((e) => ({
            message: e.message,
            path: e.path?.join('.'),
          })),
        );

        // Report to Sentry WITHOUT the submitted values (EW-T18). The ClientError itself is not
        // sent: its message embeds JSON.stringify({ response, request }), variables included.
        // graphqlErrorReport sends a clean Error, the variables' keys-only shape, and the errors'
        // message/path/codes. instrument.ts's beforeSend scrubber is the second layer.
        if (clientError) {
          const report = graphqlErrorReport(clientError, operationName ?? 'unknown');
          Sentry.captureException(report.error, report.context);
        }
        return;
      }

      // EW-FIX-47 — a response that is an Error but NOT a ClientError-with-errors
      // (e.g. a non-JSON body from a misrouted endpoint, a raw network failure, a
      // CORS/opaque response) fell through this entire function with zero
      // console/Sentry output. That silence is exactly what let a misconfigured
      // VITE_GRAPHQL_URL (schemeless, resolved relative to this app's own origin,
      // answered 200/HTML by the SPA fallback, then failed graphql-request's JSON
      // parse) fail every GraphQL query in prod for hours with no visible trace.
      console.error(`[GraphQL Error] ${operationName} — non-GraphQL-shaped failure:`, response);
      Sentry.captureException(response, {
        tags: { graphql_operation: operationName ?? 'unknown', graphql_error_code: 'non_gql_shaped' },
      });
    }
  },
});

/**
 * Generic fetcher for use with @tanstack/react-query and codegen TypedDocumentNode.
 * Auth is handled by graphqlClient's requestMiddleware (Keycloak token refresh).
 *
 * Uses the object overload of graphqlClient.request to avoid conditional
 * VariablesAndRequestHeadersArgs inference issues with generic TVariables.
 */
export function fetchGql<TData, TVariables extends Variables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  signal?: AbortSignal,
): Promise<TData> {
  const opts: RequestOptions<Variables, TData> = { document, variables, signal };
  return graphqlClient.request(opts);
}
